import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Combobox, ETypeOfNotification, Modal, setNotification } from 'medere-ui-framework';
import { TransferRequestDTO } from '../dto/FinancialAccountDTO';
import { createTransfer } from '../services/FinancialAccountController';
import { getFieldErrorMessage, INSUFFICIENT_BALANCE, parseApiError } from '../services/apiError';
import FieldError from './FieldError/FieldError';

export interface TransferAccountOption {
    financialAccount: number;
    name: string;
    displayDescription: string;
}

interface TransferModalProps {
    visible: boolean;
    onHide: () => void;
    sourceAccounts: TransferAccountOption[];
    destinationAccounts: TransferAccountOption[];
    defaultSource?: TransferAccountOption | null;
    onSuccess: () => void;
}

export default function TransferModal({
    visible,
    onHide,
    sourceAccounts,
    destinationAccounts,
    defaultSource,
    onSuccess,
}: TransferModalProps) {
    const dispatch = useDispatch();

    const [origen, setOrigen] = useState<TransferAccountOption | null>(null);
    const [destino, setDestino] = useState<TransferAccountOption | null>(null);
    const [amount, setAmount] = useState<number | null>(null);
    const [notes, setNotes] = useState('');
    const [transferring, setTransferring] = useState(false);
    const [amountError, setAmountError] = useState<string | null>(null);
    const amountRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (visible) {
            setOrigen(defaultSource ?? null);
            setDestino(null);
            setAmount(null);
            setNotes('');
            setAmountError(null);
        }
    }, [visible]);

    async function handleTransfer() {
        if (!origen || !destino) {
            dispatch(setNotification({
                title: 'Seleccione cuenta origen y destino',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        if (origen.financialAccount === destino.financialAccount) {
            dispatch(setNotification({
                title: 'Las cuentas deben ser diferentes',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        if (amount == null || amount <= 0) {
            dispatch(setNotification({
                title: 'Monto inválido',
                desc: 'El monto debe ser mayor a 0',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        setTransferring(true);
        try {
            const body: TransferRequestDTO = {
                origen: origen.financialAccount,
                destino: destino.financialAccount,
                amount,
                notes: notes.trim() || undefined,
            };
            await createTransfer(body);
            dispatch(setNotification({
                title: 'Transferencia realizada',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            onHide();
            onSuccess();
        } catch (error) {
            const apiError = parseApiError(error);
            const insufficientBalance = apiError.errorCode === INSUFFICIENT_BALANCE
                ? getFieldErrorMessage(apiError, 'amount')
                : undefined;
            if (insufficientBalance) {
                setAmountError(insufficientBalance);
                amountRef.current?.focus();
                return;
            }
            dispatch(setNotification({
                title: 'No se pudo realizar la transferencia',
                desc: apiError.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setTransferring(false);
        }
    }

    const filteredDestinations = destinationAccounts.filter(
        a => a.financialAccount !== origen?.financialAccount,
    );

    return (
        <Modal
            visible={visible}
            setVisible={(v: boolean) => { if (!v && !transferring) onHide(); }}
            header="Transferencia entre cuentas"
            width="w-medium"
            closable={!transferring}
        >
            <div className="flex flex-column gap-3">
                <div className="cc-form-grid">
                    <div className="cc-form-field">
                        <Combobox
                            label="Cuenta origen *"
                            list={sourceAccounts}
                            value={origen}
                            setValue={(v: TransferAccountOption | null) => {
                                setOrigen(v);
                                setAmountError(null);
                            }}
                            optionLabel="displayDescription"
                            placeholder="Seleccionar origen..."
                        />
                    </div>
                    <div className="cc-form-field">
                        <Combobox
                            label="Cuenta destino *"
                            list={filteredDestinations}
                            value={destino}
                            setValue={(v: TransferAccountOption | null) => setDestino(v)}
                            optionLabel="displayDescription"
                            placeholder="Seleccionar destino..."
                        />
                    </div>
                </div>
                <div className="cc-form-field">
                    <label htmlFor="transfer-amount">Monto *</label>
                    <InputNumber
                        inputId="transfer-amount"
                        inputRef={amountRef}
                        value={amount}
                        onValueChange={e => {
                            setAmount(e.value ?? null);
                            setAmountError(null);
                        }}
                        mode="currency"
                        currency="ARS"
                        locale="es-AR"
                        min={0}
                        disabled={transferring}
                        className={amountError ? 'field-error-target' : undefined}
                        aria-invalid={amountError ? true : undefined}
                        aria-describedby={amountError ? 'transfer-amount-error' : undefined}
                    />
                    <FieldError id="transfer-amount-error" message={amountError} />
                </div>
                <div className="cc-form-field">
                    <label>Notas</label>
                    <InputTextarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        rows={2}
                        autoResize
                        placeholder="Opcional"
                        disabled={transferring}
                    />
                </div>
                <div className="flex justify-content-end gap-2 mt-2">
                    <Button
                        label="Cancelar"
                        className="buttonMain3"
                        onClick={onHide}
                        disabled={transferring}
                    />
                    <Button
                        label="Transferir"
                        icon="pi pi-arrow-right-arrow-left"
                        className="buttonMain"
                        onClick={handleTransfer}
                        loading={transferring}
                        disabled={
                            !origen ||
                            !destino ||
                            amount == null ||
                            amount <= 0
                        }
                    />
                </div>
            </div>
        </Modal>
    );
}
